import { useState, useEffect, useRef } from 'react';
import {
  searchAliases,
  createAlias,
  updateAlias,
  deactivateAlias,
  exportAliases
} from '../services/api/aliasApi';
import { getCustomersForDropdown } from '../services/api/customerApi';

export const useAliases = () => {
  const [aliases, setAliases] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedFieldKey, setSelectedFieldKey] = useState('');
  const hasFetchedCustomers = useRef(false);

  // Fetch customers on mount
  useEffect(() => {
    if (!hasFetchedCustomers.current) {
      hasFetchedCustomers.current = true;
      fetchCustomers();
    }
  }, []);

  // Fetch aliases when customer or field key changes
  useEffect(() => {
    if (selectedCustomerId) {
      fetchAliases();
    } else {
      setAliases([]);
    }
  }, [selectedCustomerId, selectedFieldKey]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomersForDropdown();
      const customerList = response.data.items || response.data || [];
      setCustomers(customerList);
      
      // Auto-select first customer if available
      if (customerList.length > 0) {
        setSelectedCustomerId(customerList[0].id);
      }
    } catch (err) {
      setError('Failed to fetch customers');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAliases = async () => {
    if (!selectedCustomerId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await searchAliases(
        selectedCustomerId,
        selectedFieldKey || null,
        null
      );
      setAliases(response.data || []);
    } catch (err) {
      setError('Failed to fetch aliases');
      console.error('Error fetching aliases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlias = async (aliasData) => {
    try {
      setError(null);
      await createAlias(aliasData);
      setSuccess('Alias created successfully!');
      setTimeout(() => setSuccess(null), 3000);
      await fetchAliases();
    } catch (err) {
      setError('Failed to create alias');
      console.error('Error creating alias:', err);
      throw err;
    }
  };

  const handleDeactivateAlias = async (aliasId) => {
    try {
      setError(null);
      await deactivateAlias(aliasId);
      setSuccess('Alias deactivated successfully!');
      setTimeout(() => setSuccess(null), 3000);
      await fetchAliases();
    } catch (err) {
      setError('Failed to deactivate alias');
      console.error('Error deactivating alias:', err);
      throw err;
    }
  };

  const handleUpdateAlias = async (aliasId, updateData) => {
    try {
      setError(null);
      await updateAlias(aliasId, updateData);
      setSuccess('Alias updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
      await fetchAliases();
    } catch (err) {
      setError('Failed to update alias');
      console.error('Error updating alias:', err);
      throw err;
    }
  };

  const handleExportAliases = async () => {
    try {
      setError(null);
      const response = await exportAliases(
        selectedCustomerId,
        selectedFieldKey || null
      );
      
      // Create a download link for the JSON file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aliases_${selectedCustomerId}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('Aliases exported successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to export aliases');
      console.error('Error exporting aliases:', err);
      throw err;
    }
  };

  return {
    aliases,
    customers,
    loading,
    error,
    success,
    selectedCustomerId,
    selectedFieldKey,
    setSelectedCustomerId,
    setSelectedFieldKey,
    fetchAliases,
    createAlias: handleCreateAlias,
    updateAlias: handleUpdateAlias,
    deactivateAlias: handleDeactivateAlias,
    exportAliases: handleExportAliases,
  };
};
